import React from 'react';
import { Plus, Minus } from 'lucide-react';
import { MenuItem } from '../../services/dataService';
import { CartItem } from './publicMenuTypes';

type Props = {
  item: MenuItem;
  isTableMode: boolean;
  customerName: string;
  quantityInCart: number;
  cartItems: CartItem[];
  onAdd: (item: MenuItem) => void;
  onIncrement: (menuItemId: string) => void;
  onDecrement: (menuItemId: string) => void;
  compact?: boolean;
  /** Stretch primary add button to full row width (e.g. image cards). */
  fullWidth?: boolean;
};

const MenuItemCartControls: React.FC<Props> = ({
  item,
  isTableMode,
  customerName,
  quantityInCart,
  cartItems,
  onAdd,
  onIncrement,
  onDecrement,
  compact = false,
  fullWidth = false,
}) => {
  if (!isTableMode || !customerName) return null;

  const pad = compact ? 'py-2 px-2' : 'py-2.5 px-3';
  const btnSize = compact ? 'p-1.5' : 'p-2';

  if (quantityInCart === 0) {
    return (
      <button
        type="button"
        onClick={() => onAdd(item)}
        className={`rounded-lg bg-amber-500 text-white font-semibold shadow-sm transition active:scale-[0.98] hover:bg-amber-600 ${pad} text-xs tablet:text-sm ${
          fullWidth ? 'w-full' : 'shrink-0'
        }`}
      >
        Add
      </button>
    );
  }

  const cartItem = cartItems.find((ci) => ci.menuItemId === item.id);

  return (
    <div
      className={`flex items-center gap-1 rounded-lg bg-zinc-100 ${pad} ${
        fullWidth ? 'w-full justify-between' : 'shrink-0'
      }`}
    >
      <button
        type="button"
        onClick={() => cartItem && onDecrement(cartItem.id)}
        className={`rounded-md bg-white text-red-600 shadow-sm transition hover:bg-red-50 ${btnSize}`}
        aria-label="Decrease quantity"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="min-w-[1.25rem] text-center text-xs font-bold text-zinc-900">{quantityInCart}</span>
      <button
        type="button"
        onClick={() => cartItem && onIncrement(cartItem.id)}
        className={`rounded-md bg-white text-emerald-600 shadow-sm transition hover:bg-emerald-50 ${btnSize}`}
        aria-label="Increase quantity"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default MenuItemCartControls;
